import { useState, useCallback } from 'react';
import { useTicketResumeMutation } from '../../../../../services/ticketEasyService/ticketEasy-api';
import { useMyProfileQuery } from '../../../../../services/profileService/profile-api';
import { TicketResumeCommand } from '../../../../../services/ticketEasyService/commands/approval/TicketResumeCommand';

interface UseTicketResumeProps {
  ticketId?: string;
  onSuccess?: () => void;
}

export function useTicketResume({ ticketId, onSuccess }: UseTicketResumeProps) {
  const [resumeDialogOpen, setResumeDialogOpen] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [resumeSuccess, setResumeSuccess] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  
  const { data: myProfile } = useMyProfileQuery();
  const [resumeTicket] = useTicketResumeMutation();
  
  const openResumeDialog = useCallback(() => {
    setResumeDialogOpen(true);
  }, []);
  
  const closeResumeDialog = useCallback(() => {
    setResumeDialogOpen(false);
  }, []);
  
  const handleResumeSubmit = useCallback(async (comments: string) => {
    if (!ticketId) {
      setResumeError("Cannot resume ticket: Missing ticket ID");
      return;
    }
    
    if (!myProfile) {
      setResumeError("Cannot resume ticket: Missing user data");
      return;
    }
    
    setResuming(true);
    setResumeError(null);
    
    try {
      const resumeCommand = new TicketResumeCommand({
        ticketId,
        resumedBy: myProfile.identity,
        resumedByEmail: myProfile.email,
        comments
      });
      
      await resumeTicket(resumeCommand).unwrap();
      setResumeSuccess(true);
      closeResumeDialog();
      
      if (onSuccess) {
        onSuccess();
      }
      
      // Auto-hide success message after 6 seconds
      setTimeout(() => {
        setResumeSuccess(false);
      }, 6000);
    } catch (error) {
      console.error("Failed to resume ticket:", error);
      setResumeError("Failed to resume ticket. Please try again.");
    } finally {
      setResuming(false);
    }
  }, [ticketId, myProfile, resumeTicket, closeResumeDialog, onSuccess]);
  
  const clearSuccessMessage = useCallback(() => {
    setResumeSuccess(false);
  }, []);
  
  const clearErrorMessage = useCallback(() => {
    setResumeError(null);
  }, []);
  
  return {
    resumeDialogOpen,
    resuming,
    resumeSuccess,
    resumeError,
    openResumeDialog,
    closeResumeDialog,
    handleResumeSubmit,
    clearSuccessMessage,
    clearErrorMessage
  };
}
